import moment, { Moment } from 'moment';
import { renderStandardVersionLabel, CompareListColumns, renderCharacterInputValue } from '@/presentation/components/compare/list';
import { CompareItem } from '@/presentation/components/compare/store';
import { Dashboard, loadDashboard, newLoadDashboard } from '@/proto/dashboard';
import { StandardCommonFieldKey, Stats, FieldKey } from '@/proto/field';
import {
  MetricSubType,
  MetricType,
  TimeType,
} from '@/proto/metric';


export interface CompareData {
  startTime: Moment,
  endTime: Moment,
  days: number,
  list: {
    data: Record<string, string | number>;
    head: string;
    extra?: any;
  }[],
  timeWindow?: TimeType;
}

export const getColumnStringByStandardFields = (columns: CompareListColumns[], fields: Record<string, any>) => {
  const format = (key: string) => {
    if (key === StandardCommonFieldKey.TimeRange) {
      const start = moment(fields[StandardCommonFieldKey.TimeRange]?.date_value?.[0]).format('YYYY/MM/DD');
      const end = moment(fields[StandardCommonFieldKey.TimeRange]?.date_value?.[1]).format('YYYY/MM/DD');
      return `${start}~${end}`;
    }

    const value = fields[key];
    return renderStandardVersionLabel(value);
  };

  return columns.filter(column => !column.disabled)
    .map(column => format(column.dataIndex))
    .join(' - ');
};
export const DEFAULT_MINUTE_FORMATTER = 'YYYY/MM/DD HH:mm';
export const DEFAULT_FORMATTER = 'YYYY/MM/DD';

export const getColumnString = (columns: CompareListColumns[], fields: Record<string, any>) => {
  const format = (key: string) => {
    if (key === FieldKey.TimeRange) {
      return `${fields[FieldKey.TimeRange][0]}~${fields[FieldKey.TimeRange][1]}`;
    }

    return renderCharacterInputValue(fields[key]);
  };

  return columns.filter(column => !column.disabled)
    .map(column => format(column.dataIndex))
    .join(' - ');
};

export const getListDataByStandardFields = (data: Dashboard[], compareList: CompareItem[]) => {
  const res: { data: any; head: string; }[] = [];
  data.forEach((item, indexNum) => {
    const { id, fields = {} } = compareList[indexNum];
    const formatter = [TimeType.HOUR, TimeType.MINUTE5].includes(fields?.time_window || fields?.time_window_type)
      ? DEFAULT_MINUTE_FORMATTER : DEFAULT_FORMATTER;
    const { timePoints } = item;
    item.data.forEach((i: { dataPoints: (string | number)[]; metricName: string; }) => {
      if (i.metricName !== 'count') {
        const temp: any = {};
        i.dataPoints.forEach((point, index) => {
          temp[moment(timePoints[index]).format(formatter)] = point;
        });
        res.push({
          data: temp,
          head: id,
        });
      }
    });
  });

  return res;
};

export const getListData = (data: Dashboard[], compareList: CompareItem[]) => {
  const res: { data: any; head: string; }[] = [];
  data.forEach((item, indexNum) => {
    const { id, fields = {} } = compareList[indexNum];
    const formatter = [TimeType.HOUR, TimeType.MINUTE5].includes(fields?.time_window || fields?.time_window_type)
      ? DEFAULT_MINUTE_FORMATTER : DEFAULT_FORMATTER;
    const { timePoints } = item;
    item.data.forEach((i: { dataPoints: (string | number)[]; metricName: string; }) => {
      if (i.metricName !== 'count') {
        const temp: any = {};
        i.dataPoints.forEach((point, index) => {
          temp[moment(timePoints[index]).format(formatter)] = point;
        });
        res.push({
          data: temp,
          head: id,
        });
      }
    });
  });

  return res;
};

export const getCompareData = async (
  type: MetricType,
  subTypes: MetricSubType[],
  compareList: CompareItem[],
  stats?: Stats,
  token?: string,
): Promise<CompareData> => {
  const data = await Promise
    .all(compareList.map(async item => (token
      ? await newLoadDashboard(type, subTypes, item.token || token, stats)
      : await loadDashboard(type, subTypes, item.fields, stats))));
  const startTime = (fields: Record<string, any>) => moment(fields[FieldKey.TimeRange][0]);
  const endTime = (fields: Record<string, any>) => moment(fields[FieldKey.TimeRange][1]);
  return {
    startTime: moment.min(compareList.map(item => startTime(item.fields))),
    endTime: moment.max(compareList.map(item => endTime(item.fields))),
    days: Math.max(...compareList.map(item => (endTime(item.fields).diff(startTime(item.fields), 'days') + 1))),
    list: getListData(data, compareList),
    timeWindow: compareList[0]?.fields?.time_window || compareList[0]?.fields?.time_window_type,
  };
};

export const getCSVCompareData = (data: CompareData) => {
  const body: any[] = [];
  for (let time = data.startTime.clone(); time.isSameOrBefore(data.endTime, 'date'); time.add(1, 'days')) {
    const line = [];
    const key = time.format('YYYY-MM-DD');
    line.push(key);
    data.list.forEach((item) => {
      line.push(item.data?.[key] ?? '--');
    });
    body.push(line);
  }
  const head = ['时间'];
  data.list.forEach((item) => {
    head.push(item.head);
  });
  return {
    head: [head],
    body,
  };
};
